import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, lastValueFrom, map, of, tap } from 'rxjs';
import dayjs from 'dayjs';
import { environment } from '@env/environment';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import {
  MessageDetailItem,
  MessageItem,
  MessageDetailResponse,
  MessageListResponse,
  MessageResendResponse,
  MessagingResendPayload
} from '../models/order-messaging.model';

export interface FetchMessagesParams {
  tenantId: string;
  orderId: string;
}

@Injectable({ providedIn: 'root' })
export class OrderMessagingService {
  private readonly http = inject(HttpClient);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly endpoint = `${environment.API_MESSAGING}`;

  async fetchMessages(params: FetchMessagesParams): Promise<MessageItem[]> {
    const { tenantId, orderId } = params;
    const url = `${this.endpoint}/api/customer-care/v1/${tenantId}/messaging/messages/${orderId}`;

    const dateRange = this.tenantUrlUtil.getDateRangeForAPI();

    let httpParams = new HttpParams();
    if (dateRange) {
      httpParams = httpParams
        .set('date-from', dateRange.dateFrom)
        .set('date-to', dateRange.dateTo);
    }

    return lastValueFrom(
      this.http.get<MessageListResponse>(url, { params: httpParams }).pipe(
        map(res => res.data || []),
        catchError(error => {
          if (error.status === 404) {
            return of([]);
          }
          throw error;
        })
      )
    );
  }

  // Método puro para obtener detalle de mensaje
  async fetchMessageDetail(tenantId: string, emailId: string): Promise<MessageDetailItem[]> {
    const url = `${this.endpoint}/api/customer-care/v1/${tenantId}/messaging/messages/detail/${emailId}`;

    return lastValueFrom(
      this.http.get<MessageDetailResponse>(url).pipe(
        map(res => res.data || []),
        catchError(error => {
          if (error.status === 404) {
            return of([]);
          }
          throw error;
        })
      )
    );
  }

  // Método puro para reenvío de mensaje
  async resendMessage(payload: MessagingResendPayload): Promise<MessageResendResponse> {
    const url = `${this.endpoint}/api/customer-care/v1/${payload.tenantId}/messaging/messages/resendmail`;

    return lastValueFrom(
      this.http.post<MessageResendResponse>(url, payload).pipe(
        tap(response => {
          if (response.code !== 1) {
            throw new Error(response.message || 'Error desconocido');
          }
        })
      )
    );
  }

  // Métodos helper estáticos para transformaciones
  static formatMessagesWithDateTime(messages: MessageItem[]): any[] {
    return messages.slice().sort((a, b) => {
      const dateA = dayjs(a.fecha, 'DD/MM/YYYY HH:mm:ss');
      const dateB = dayjs(b.fecha, 'DD/MM/YYYY HH:mm:ss');

      if (!dateA.isValid() && !dateB.isValid()) return 0;
      if (!dateA.isValid()) return 1;
      if (!dateB.isValid()) return -1;

      return dateB.diff(dateA);
    }).map(m => {
      const [fecha, hora] = OrderMessagingService.splitDateTime(m.fecha);
      return {
        ...m,
        tipo: m.tipoMensaje,
        fecha,
        hora,
        destinatario: m.destinatario
      };
    });
  }

  static buildTimelineSteps(messageDetail: MessageDetailItem[], messageType: string): any[] {
    const baseSteps: Record<string, any[]> = {
      EMAIL: [
        { status: 'PUB_SUB_DELIVERED', label: 'Ingresado a la cola', description: 'El mensaje entró en la cola para su envío' },
        { status: 'SENDGRID_CONFIRMED', label: 'Entregado al destinatario', description: 'El mensaje se entregó al buzón del destinatario' },
        { status: 'SENDGRID_OPENED', label: 'Apertura del correo', description: 'El usuario abrió el correo enviado a su bandeja' },
      ],
      SMS: [
        { status: 'PUB_SUB_DELIVERED', label: 'Ingresado a la cola', description: 'El mensaje entró en la cola para su envío' },
        { status: 'MOVISTAR_DELIVERED', label: 'Mensaje entregado', description: 'El mensaje se entregó al buzón del destinatario <Movistar>' },
      ],
      WHATSAPP: [
        { status: 'PUB_SUB_DELIVERED', label: 'Ingresado a la cola', description: 'El mensaje entró en la cola para su envío' },
        { status: 'WHATSAPP_SENT', label: 'Mensaje enviado por whatsApp', description: 'El mensaje ha sido enviado por canal, esperando recepción' },
        { status: 'WHATSAPP_DELIVERED', label: 'Mensaje entregado al destinatario', description: 'El mensaje se entregó al buzón del destinatario y presenta ✔✔' },
        { status: 'WHATSAPP_READ', label: 'Mensaje leído por el destinatario', description: 'El usuario leyó el mensaje presentando ✔✔ azules' },
      ]
    };

    const channel = messageType?.toUpperCase() ?? 'EMAIL';
    const grouped = new Map(messageDetail.map(item => [item.status, item]));
    const steps = baseSteps[channel] ?? [];

    let lastMatchedIndex = -1;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (grouped.has(steps[i].status)) {
        lastMatchedIndex = i;
        break;
      }
    }

    return steps.map((step, index) => {
      const match = grouped.get(step.status);
      return {
        status: step.status,
        statusName: step.label,
        statusDescription: step.description,
        createdAt: match ? OrderMessagingService.formatDate(match.createdAt) : null,
        done: !!match,
        active: index <= lastMatchedIndex
      };
    });
  }

  private static splitDateTime(datetime: string): [string, string] {
    const parsedDate = dayjs(datetime, 'DD/MM/YYYY HH:mm:ss');
    if (!parsedDate.isValid()) {
      return ['--', '--'];
    }
    return [parsedDate.format('DD/MM/YYYY'), parsedDate.format('hh:mm:ss A')];
  }

  private static formatDate(datetime: string): string {
    const parsedDate = dayjs(datetime, 'DD/MM/YYYY HH:mm:ss A');
    if (!parsedDate.isValid()) {
      return '--';
    }
    return parsedDate.format('DD/MM/YY hh:mm:ss A');
  }
}
